import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BadgeCheck,
  Bell,
  Building2,
  Check,
  ChevronDown,
  GitBranch,
  LogOut,
  Plus,
  Settings,
} from "lucide-react";
import { API } from "../../constant/api";
import { useAuth } from "../../context/auth-context";
import { useUser } from "../../context/user-context";
import {
  BusinessListItem,
  createBusinessBlockedMessage,
  useBusinesses,
} from "../../hooks/use-businesses";
import useAuthAxios from "../../utils/baseAxios";
import { cn } from "../../utils/cn";
import NotificationModal from "../NotificationModal";
import PageAvatar from "../PageAvatar";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Separator } from "../ui/separator";
import { SidebarTrigger } from "../ui/sidebar";
import { navItems } from "./nav-items";

const statusLabel = (status?: string) => {
  const value = String(status ?? "").toUpperCase();
  if (!value) return null;
  if (value === "APPROVED" || value === "VERIFIED") return "Approved";
  if (value === "PENDING") return "Pending";
  if (value === "REJECTED") return "Rejected";
  return value.charAt(0) + value.slice(1).toLowerCase();
};

const AppNavbar = () => {
  const { isAuthenticated, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isNotified, setIsNotified, merchantDetails } = useUser();
  const axiosInstance = useAuthAxios();

  const {
    businesses,
    business,
    businessId,
    selectBusiness,
    canCreateMore,
    blockedReason,
    meta,
  } = useBusinesses();

  const profile = merchantDetails?.user?.profile;
  const firstName = profile?.firstName ?? "";
  const lastName = profile?.lastName ?? "";
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "--";
  const fullName = `${firstName} ${lastName}`.trim() || "Merchant";
  const businessName = business?.name as string | undefined;
  const businessLogo = business?.logo;
  const isBranch = !!business?.parentBusinessId;

  const groupedBusinesses = useMemo(() => {
    if (!businesses) return [];
    const parents = businesses.filter((b: BusinessListItem) => !b.parentBusinessId);
    const branches = businesses.filter((b: BusinessListItem) => !!b.parentBusinessId);

    const result: (BusinessListItem & { _isBranch?: boolean; _parentName?: string })[] = [];
    for (const parent of parents) {
      result.push(parent);
      const childBranches = branches.filter(
        (b: BusinessListItem) => b.parentBusinessId === parent._id
      );
      for (const branch of childBranches) {
        result.push({ ...branch, _isBranch: true, _parentName: parent.name });
      }
    }
    const orphanBranches = branches.filter(
      (b: BusinessListItem) => !parents.some((p) => p._id === b.parentBusinessId)
    );
    for (const branch of orphanBranches) {
      result.push({ ...branch, _isBranch: true });
    }
    return result;
  }, [businesses]);

  // The API returns email as an object — { address, isVerified } — not a
  // string. Rendering it directly throws "Objects are not valid as a React
  // child". Accept either shape in case the payload ever changes.
  const rawEmail = merchantDetails?.user?.email;
  const email: string | undefined =
    typeof rawEmail === "string" ? rawEmail : rawEmail?.address;
  const emailVerified: boolean =
    typeof rawEmail === "object" && !!rawEmail?.isVerified;

  const fetchUnreadNotifications = async () => {
    const response = await axiosInstance.get(API.getNotifications(1));
    return response.data;
  };

  const { data: notificationsData } = useQuery({
    queryKey: ["unreadNotifications"],
    queryFn: fetchUnreadNotifications,
    enabled: isAuthenticated,
  });

  const unreadNotify =
    notificationsData?.data?.meta?.noOfUnreadInAppNotifications ?? 0;

  const pageTitle =
    navItems.find((item) => pathname.startsWith(item.href))?.title ?? "";

  const handleAddBusiness = () => {
    if (!canCreateMore) {
      return;
    }
    navigate("/business");
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4 font-manrope lgss:px-8">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />
      <h1 className="font-sora text-[16px] font-semibold text-foreground">
        {pageTitle}
      </h1>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {businesses && businesses.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Switch business"
                className="flex max-w-[160px] items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1.5 text-left outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:max-w-[220px]"
              >
                {isBranch ? (
                  <GitBranch className="size-3.5 shrink-0 text-primary" />
                ) : businessLogo ? (
                  <PageAvatar
                    name={businessName}
                    image={businessLogo}
                    className="size-5"
                    textClassName="text-[10px]"
                  />
                ) : (
                  <Building2 className="size-3.5 shrink-0 text-primary" />
                )}
                <span className="truncate font-sora text-[13px] font-semibold text-foreground">
                  {businessName ?? "Select business"}
                </span>
                {isBranch && (
                  <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                    Branch
                  </span>
                )}
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 font-manrope">
              <DropdownMenuLabel className="font-normal">
                <p className="font-sora text-sm font-semibold text-foreground">
                  Your businesses
                </p>
                <p className="text-xs text-muted-foreground">
                  {meta.count} of {meta.max} used
                </p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {groupedBusinesses.map((item) => {
                const selected = item._id === businessId;
                const label = statusLabel(item.status);
                const itemIsBranch = !!(item as any)._isBranch;
                const parentName = (item as any)._parentName;
                return (
                  <DropdownMenuItem
                    key={item._id}
                    onClick={() => selectBusiness(item._id)}
                    className={cn(
                      "cursor-pointer items-start gap-2 py-2",
                      itemIsBranch && "pl-8"
                    )}
                  >
                    <Check
                      className={cn(
                        "mt-0.5 size-4 shrink-0",
                        selected ? "opacity-100 text-primary" : "opacity-0"
                      )}
                    />
                    {itemIsBranch ? (
                      <GitBranch className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                    ) : (
                      <PageAvatar
                        name={item.name}
                        image={item.logo}
                        className="size-7"
                        textClassName="text-[12px]"
                      />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {item.name || "Untitled business"}
                      </span>
                      {itemIsBranch && parentName && (
                        <span className="block truncate text-[11px] font-medium text-primary/70">
                          Branch of {parentName}
                        </span>
                      )}
                      {label && (
                        <span
                          className={cn(
                            "mt-0.5 block text-[11px] font-medium",
                            String(item.status).toUpperCase() === "PENDING"
                              ? "text-amber-600"
                              : String(item.status).toUpperCase() === "REJECTED"
                                ? "text-destructive"
                                : "text-muted-foreground"
                          )}
                        >
                          {label}
                          {item.type ? ` · ${item.type}` : ""}
                        </span>
                      )}
                    </span>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!canCreateMore}
                onClick={handleAddBusiness}
                className="cursor-pointer"
                title={
                  !canCreateMore
                    ? createBusinessBlockedMessage(blockedReason)
                    : undefined
                }
              >
                <Plus className="mr-2 size-4" />
                Add business
              </DropdownMenuItem>
              {!canCreateMore && (
                <p className="px-2 pb-2 text-[11px] leading-snug text-muted-foreground">
                  {createBusinessBlockedMessage(blockedReason)}
                </p>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button
          variant="ghost"
          size="icon"
          aria-label={`Notifications${unreadNotify >= 1 ? ", unread" : ""}`}
          onClick={() => setIsNotified((prev: boolean) => !prev)}
          className="relative rounded-full bg-muted hover:bg-accent"
        >
          <Bell className="size-5" />
          {unreadNotify >= 1 && (
            <span className="absolute right-2 top-2 size-2 animate-pulse rounded-full bg-primary" />
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Avatar className="size-[38px]">
                <AvatarFallback className="bg-accent text-[13px] font-semibold text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 font-manrope">
            <DropdownMenuLabel className="font-normal">
              {businessName && (
                <p className="flex items-center gap-1.5 truncate font-sora text-sm font-bold text-foreground">
                  <Building2 className="size-3.5 shrink-0 text-primary" />
                  {businessName}
                </p>
              )}
              <p className="mt-1 truncate text-[13px] font-medium text-foreground">
                {fullName}
              </p>
              {email && (
                <p className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <span className="truncate">{email}</span>
                  {emailVerified && (
                    <BadgeCheck
                      className="size-3 shrink-0 text-success"
                      aria-label="Email verified"
                    />
                  )}
                </p>
              )}
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 size-4" />
              Settings
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <LogOut className="mr-2 size-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isNotified && <NotificationModal setIsNotified={setIsNotified} />}
    </header>
  );
};

export default AppNavbar;
